import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import integrationServices from '../services/integrationServices';

const CheckEquipmentModal = ({ open, onClose, publicId }) => {
  const [loading, setLoading] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [fullReportText, setFullReportText] = useState('');
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open && publicId) {
      fetchData();
    }
    if (!open) {
      setEquipment([]);
      setFullReportText('');
      setError('');
    }
  }, [open, publicId]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [equipRes, recogRes] = await Promise.allSettled([
        integrationServices.getWarehouseEquipment(publicId),
        integrationServices.getRecognitionData(publicId),
      ]);

      if (equipRes.status === 'fulfilled') {
        const rows = equipRes.value?.data || [];
        const allItems = Array.isArray(rows) ? rows : [];
        setEquipment(allItems.filter((item) =>
          !item.need_to_return && !item.returned && !item.damaged && !item.replaced_by_warranty
        ));
      }

      if (recogRes.status === 'fulfilled') {
        const data = recogRes.value?.data;
        setFullReportText(data?.full_report_text || '');
      }
    } catch (err) {
      setError('Failed to load equipment data');
    } finally {
      setLoading(false);
    }
  };

  const hasData = fullReportText || equipment.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">
          Equipment Status — {publicId}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
        ) : !hasData ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No equipment received yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Equipment data will appear here once it is registered in the Warehouse.
            </Typography>
          </Box>
        ) : (
          <Box>
            {fullReportText && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recognition Report
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: 300,
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <pre style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}>
                    {fullReportText}
                  </pre>
                </Paper>
              </Box>
            )}

            {equipment.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Warehouse Equipment ({equipment.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Serial Number</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>In Stock</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {equipment.map((item, idx) => (
                        <TableRow key={item.id || idx}>
                          <TableCell>{item.model || item.description || '-'}</TableCell>
                          <TableCell>{item.serial_number || item.serial || '-'}</TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {item.in_stock || item.is_in_stock ? (
                              <Chip icon={<CheckCircleIcon />} label="Yes" size="small" color="success" variant="outlined" />
                            ) : (
                              <Chip icon={<CancelIcon />} label="No" size="small" color="default" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckEquipmentModal;
